import { Project } from "shared/datamodel/schemas/project";
import style from "./section-title.module.css";
import { Team } from "shared/datamodel/schemas/team";
import { useEffect, useMemo, useRef } from "react";
import { BoardSection, BoardSectionType } from "shared/datamodel/schemas/board-section";
import InviteModalNew from "frontend/modals/invite-modal-new";
import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import { useTeamsCache } from "frontend/hooks/caching/use-teams-cache";
import { useHover, useOnClickOutside } from "usehooks-ts";
import ManageCollaborationMembers from "frontend/modals/collaboration/manage-collaboration-members";
import AccountMembers from "frontend/modals/collaboration/account-members";
import { ThreeDots } from "frontend/icons/three-dots";
import { CanvasTemplate } from "shared/datamodel/schemas/canvas-template";
import Modal from "frontend/modal/modal";
import { InstanceType } from "shared/datamodel/schemas/ability";
import StyledButton, { ButtonStyle } from "frontend/ui-components/styled-button";
import { CollaborationType, InviteMode, projectCacheAtom, teamCacheAtom } from "state-atoms";
import LeaveCollaborationModalNew from "frontend/modals/collaboration/leave-collaboration-modal-new";
import DeleteCollaborationModalNew from "frontend/modals/collaboration/delete-collaboration-modal-new";
import { useTeamsWithProjects } from "frontend/hooks/caching/use-teams-with-projects";
import { TemplatesIcon } from "frontend/icons/templates";
import InstanceMenu from "./side-menu/instance-menu";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { MyCanvasesBigIcon } from "frontend/icons/my-canvases-big";
import { ShareWithMeBigIcon } from "frontend/icons/share-with-me-big";
import { ProjectBigIcon } from "frontend/icons/project-big";
import { useInstanceMembers } from "frontend/hooks/use-instance-members";
import useSafeState from "frontend/hooks/use-safe-state";
import RenameModal from "frontend/modals/collaboration/rename-modal";
import { useAtomValue } from "jotai";

const membersButtonStyle = {
  borderRadius: 6,
  marginLeft: 18,
  marginRight: 9,
  height: 32,
  border: "1px solid #1973FF",
  color: "#1973FF",
  textAlign: "center" as const,
  fontFamily: "Poppins",
  fontSize: 12,
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "normal",
  letterSpacing: 0.21,
};

export function SectionTitle({
  section,
  onEditSeats,
  createNewBoard,
  onDeleteSelectedTeam,
  onDeleteSelectedProject,
  setSelectedTeam,
}: {
  section: BoardSection;
  onEditSeats: () => void;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onDeleteSelectedTeam: () => void;
  onDeleteSelectedProject: () => void;
  setSelectedTeam: (teamId: string) => void;
}) {
  const [instance, setInstance] = useSafeState<Team | Project | null>(null);
  const [showMenu, setShowMenu] = useSafeState(false);
  const [showInviteModal, setShowInviteModal] = useSafeState(false);
  const [showMembersModal, setShowMembersModal] = useSafeState(false);
  const [showLeaveModal, setShowLeaveModal] = useSafeState(false);
  const [showDeleteModal, setShowDeleteModal] = useSafeState(false);
  const [showRenameModal, setShowRenameModal] = useSafeState(false);
  const [instanceName, setInstanceName] = useSafeState<string | null>(null);
  const { teamsWithProjects, getProjectsParent } = useTeamsWithProjects();

  const teamsWithProjectsArray = teamsWithProjects ?? [];

  const { membersCount, isLoading: isMembersLoading } = useInstanceMembers(
    section.id,
    section.type === BoardSectionType.WORKSPACE ? InstanceType.Team : InstanceType.Project
  );

  const menuButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const membersButtonRef = useRef<HTMLDivElement>(null);
  const hoverMenu = useHover(menuButtonRef);
  const hoverMembers = useHover(membersButtonRef);

  const { getTeams, updateTeam: handleEditTeamName } = useTeamsCache();
  const { getProjects, updateProject: handleEditProjectName } = useProjectsCache();
  const teamsAtom = useAtomValue(teamCacheAtom);
  const projectsAtom = useAtomValue(projectCacheAtom);

  const membersButtonLabel = useMemo(() => {
    let label: string | number = "Loading";
    let noun = "members";

    if (membersCount !== undefined && !isMembersLoading && membersCount > 0) {
      label = membersCount;
      noun = membersCount === 1 ? "Member" : "Members";
    }

    return `${label} ${noun}`;
  }, [membersCount, isMembersLoading]);

  const instanceType = section.type === BoardSectionType.WORKSPACE ? InstanceType.Team : InstanceType.Project;
  const collaborationType =
    section.type === BoardSectionType.WORKSPACE ? CollaborationType.Workspace : CollaborationType.Project;

  const getInstanceName = useMemo(() => {
    if (section.type === BoardSectionType.WORKSPACE) {
      return teamsWithProjectsArray.find((team) => team.documentId === section.id)?.name;
    } else if (section.type === BoardSectionType.PROJECT) {
      const team = teamsWithProjectsArray.find((team) =>
        team.projects?.find((project) => project.documentId === section.id)
      );
      return team?.projects?.find((project) => project.documentId === section.id)?.name;
    }
  }, [section.type, section.id, teamsWithProjectsArray]);

  useEffect(() => {
    setInstanceName(getInstanceName ?? null);
  }, [getInstanceName]);

  const getWorkspace = () => {
    if (section.id) {
      return getProjectsParent(section.id);
    }
    return null;
  };

  const workspace = section.type === BoardSectionType.PROJECT ? getWorkspace() : null;

  useOnClickOutside(menuRef, () => {
    setShowMenu(false);
  });

  const fetchInstance = async () => {
    if (section.type === BoardSectionType.WORKSPACE && section.id) {
      const teams = await getTeams([section.id]);
      setInstance(teams[0] || null);
    }
    if (section.type === BoardSectionType.PROJECT && section.id) {
      const projects = await getProjects([section.id]);
      setInstance(projects[0] || null);
    }
  };

  useEffect(() => {
    if (section.type === BoardSectionType.WORKSPACE || section.type === BoardSectionType.PROJECT) {
      fetchInstance();
    }
  }, [section.id, section.type, teamsAtom, projectsAtom]);

  function renameClicked(event: React.MouseEvent) {
    event.stopPropagation();
    setShowMenu(false);
    setShowRenameModal(true);
  }

  function renderMembersModal() {
    if (showMembersModal && instance) {
      return (instance as Team)?.accountTeam ? (
        <Modal dimBackground={true}>
          <AccountMembers
            title={instance.name}
            onDismiss={() => {
              setShowMembersModal(false);
            }}
            onEditSeats={onEditSeats}
          />
        </Modal>
      ) : (
        <Modal dimBackground={true}>
          <ManageCollaborationMembers
            onDismiss={() => {
              setShowMembersModal(false);
            }}
            instanceId={instance.documentId}
            type={instanceType}
            onEditSeats={onEditSeats}
          />
        </Modal>
      );
    } else {
      return null;
    }
  }

  const renderMenu = () => {
    return (
      <InstanceMenu
        menuButtonRef={menuButtonRef}
        menuRef={menuRef}
        setShowMenu={setShowMenu}
        project={section.type === BoardSectionType.PROJECT ? (instance as Project) : null}
        team={section.type === BoardSectionType.WORKSPACE ? (instance as Team) : (workspace as Team)}
        setShowInviteModal={setShowInviteModal}
        setShowMembersModal={setShowMembersModal}
        createNewBoard={createNewBoard}
        renameClicked={(e) => renameClicked(e)}
        setShowLeaveModal={setShowLeaveModal}
        setShowDeleteModal={setShowDeleteModal}
        source="section-menu"
      />
    );
  };

  const editSectionName = (newName: string) => {
    setInstanceName(newName);
    if (section.type === BoardSectionType.WORKSPACE) {
      setInstance({ ...instance, name: newName } as Team);
      handleEditTeamName({ ...instance, name: newName });
    } else if (section.type === BoardSectionType.PROJECT) {
      setInstance({ ...instance, name: newName } as Project);
      handleEditProjectName({ ...instance, name: newName });
    }
  };

  const renderDeleteModal = () => {
    if (showDeleteModal && instance && section.type === BoardSectionType.WORKSPACE) {
      return (
        <Modal dimBackground={true}>
          <DeleteCollaborationModalNew
            type={collaborationType}
            onDismiss={() => {
              setShowDeleteModal(false);
            }}
            subtitle={`All projects and canvases within this workspace will be permanently deleted. You can move canvases to another workspace or export them before deleting.`}
            team={instance as Team}
            isSelected={true}
            onDeleteSelectedCollaboration={onDeleteSelectedTeam}
          />
        </Modal>
      );
    } else if (showDeleteModal && instance && section.type === BoardSectionType.PROJECT) {
      return (
        <Modal dimBackground={true}>
          <DeleteCollaborationModalNew
            type={collaborationType}
            onDismiss={() => {
              setShowDeleteModal(false);
            }}
            subtitle={`All projects and canvases within this project will be permanently deleted. You can move canvases to another project or export them before deleting.`}
            project={instance as Project}
            isSelected={true}
            onDeleteSelectedCollaboration={onDeleteSelectedProject}
          />
        </Modal>
      );
    } else {
      return null;
    }
  };

  const renderLeaveModal = () => {
    if (showLeaveModal && instance && section.type === BoardSectionType.WORKSPACE) {
      return (
        <Modal dimBackground={true}>
          <LeaveCollaborationModalNew
            type={collaborationType}
            onDismiss={() => {
              setShowLeaveModal(false);
            }}
            subtitle={`You will lose access to "${instanceName}" workspace and it's canvases.`}
            team={instance as Team}
          />
        </Modal>
      );
    } else if (showLeaveModal && instance && section.type === BoardSectionType.PROJECT) {
      return (
        <Modal dimBackground={true}>
          <LeaveCollaborationModalNew
            type={collaborationType}
            onDismiss={() => {
              setShowLeaveModal(false);
            }}
            subtitle={`You will lose access to "${instanceName}" project and it's canvases.`}
            project={instance as Project}
          />
        </Modal>
      );
    } else {
      return null;
    }
  };

  const renderSectionName = () => {
    if (section.type === BoardSectionType.PROJECT || section.type === BoardSectionType.WORKSPACE) {
      return (
        <div className={style.renderSectionNameContainer}>
          {section.type === BoardSectionType.PROJECT && <ProjectBigIcon color="#113357" />}
          <div className={style.inlineRenameContainer}>{instanceName}</div>
        </div>
      );
    } else {
      const sectionNames: Record<
        Exclude<BoardSectionType, BoardSectionType.PROJECT | BoardSectionType.WORKSPACE>,
        { name: string; icon: () => JSX.Element }
      > = {
        [BoardSectionType.MY_BOARDS]: { name: "My Canvases", icon: () => <MyCanvasesBigIcon /> },
        [BoardSectionType.RECENT]: { name: "Recent Canvases", icon: () => <></> },
        [BoardSectionType.SHARED_WITH_ME]: { name: "Shared with me", icon: () => <ShareWithMeBigIcon /> },
        [BoardSectionType.TEMPLATES]: { name: "Templates", icon: () => <TemplatesIcon /> },
      };
      return (
        <>
          {sectionNames[section.type].icon()}
          <div className={style.sectionNameText}>{sectionNames[section.type].name}</div>
        </>
      );
    }
  };

  const renderRenameModal = () => {
    return (
      showRenameModal && (
        <Modal dimBackground={true}>
          <RenameModal
            currentName={instanceName ?? ""}
            type={instanceType}
            onDismiss={() => setShowRenameModal(false)}
            onEditName={(name: string) => {
              editSectionName(name);
            }}
          />
        </Modal>
      )
    );
  };

  return (
    <>
      <div className={style.container}>
        <div className={style.sectionTitle}>
          {renderSectionName()}
          {(section.type === BoardSectionType.PROJECT || section.type === BoardSectionType.WORKSPACE) && (
            <div className={style.sectionControls}>
              <StyledButton
                title={membersButtonLabel}
                style={ButtonStyle.outline}
                customStyle={membersButtonStyle}
                onClick={() => {
                  setShowMembersModal(true);
                  setShowMenu(false);
                }}
                ref={membersButtonRef}
                dataTestId={"members-button"}
              />
              {hoverMembers && (
                <Tooltip
                  side="top"
                  label={`Only people added can see this ${
                    section.type === BoardSectionType.WORKSPACE ? "workspace" : "project"
                  }`}
                  relativeTo={membersButtonRef}
                  customStyle={{
                    backgroundColor: "#113357",
                    borderRadius: 6,
                    color: " #FFF",
                    fontSize: 12,
                    fontWeight: 300,
                  }}
                />
              )}
              <div
                className={style.menuButton}
                onClick={() => setShowMenu(true)}
                ref={menuButtonRef}
                data-testid={"section-title-menu-button"}
                aria-disabled={!instance}
                style={{ pointerEvents: instance ? "auto" : "none" }}
              >
                <ThreeDots color="#AFB3BF" />
              </div>
              {hoverMenu && (
                <Tooltip
                  side="top"
                  label={"Options"}
                  relativeTo={menuButtonRef}
                  customStyle={{
                    backgroundColor: "#113357",
                    borderRadius: 6,
                    color: " #FFF",
                    fontSize: 12,
                    fontWeight: 300,
                  }}
                />
              )}
            </div>
          )}
        </div>
        {section.type === BoardSectionType.PROJECT && workspace && (
          <div
            className={style.workspaceName}
            onClick={() => {
              setSelectedTeam(workspace.documentId);
            }}
            data-testid={"project-parent"}
          >{`in ${workspace.name}`}</div>
        )}
      </div>

      {showMenu && renderMenu()}
      {showInviteModal && instance && (
        <Modal dimBackground={true}>
          <InviteModalNew
            instance={instance}
            onDismiss={() => setShowInviteModal(false)}
            inviteMode={InviteMode.Edit}
            onEditSeats={onEditSeats}
            source={`${section.type}-invite-modal`}
            type={instanceType}
            parentInstance={instanceType === InstanceType.Project ? (workspace as Team) : undefined}
          />
        </Modal>
      )}
      {renderMembersModal()}
      {renderLeaveModal()}
      {renderDeleteModal()}
      {renderRenameModal()}
    </>
  );
}
